import React, { useState, useEffect } from 'react';
import { Target, Plus, Search, Filter, Download, Calendar, TrendingUp, Users, BarChart3 } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import PerformanceOverview from './components/PerformanceOverview';
import PerformanceFilters from './components/PerformanceFilters';
import EmployeePerformanceCard from './components/EmployeePerformanceCard';
import GoalManagement from './components/GoalManagement';
import ReviewCycleManagement from './components/ReviewCycleManagement';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import PerformanceDashboard from './components/PerformanceDashboard';
import { performanceApi, employeesApi, departmentsApi } from '../../lib/api';

const PerformanceManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [performanceData, setPerformanceData] = useState([]);
  const [reviewCycles, setReviewCycles] = useState([]);
  const [goals, setGoals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, goals, reviews, analytics
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    department: '',
    manager: '',
    reviewPeriod: '',
    performanceRating: '',
    goalStatus: ''
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadEmployees(),
        loadPerformanceData(),
        loadReviewCycles(),
        loadGoals(),
        loadDepartments()
      ]);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await employeesApi?.list();
      setEmployees(data || []);
    } catch (err) {
      setEmployees([]); // Empty state for demo
    }
  };

  const loadPerformanceData = async () => {
    try {
      const data = await performanceApi?.getPerformanceData();
      setPerformanceData(data || []);
    } catch (err) {
      setPerformanceData([]); // Empty state for demo
    }
  };

  const loadReviewCycles = async () => {
    try {
      const data = await performanceApi?.getCycles();
      setReviewCycles(data || []);
    } catch (err) {
      setReviewCycles([]); // Empty state for demo
    }
  };

  const loadGoals = async () => {
    try {
      const data = await performanceApi?.getAllGoals();
      setGoals(data || []);
    } catch (err) {
      setGoals([]); // Empty state for demo
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await departmentsApi?.list();
      setDepartments(data || []);
    } catch (err) {
      setDepartments([]); // Empty state for demo
    }
  };

  // Filter employees based on search and filters
  const filteredEmployees = employees?.filter(employee => {
    const matchesSearch = !searchTerm || 
      `${employee?.firstName || ''} ${employee?.lastName || ''}`?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      employee?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      employee?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesDepartment = !filters?.department || employee?.departmentId === filters?.department;
    const matchesManager = !filters?.manager || employee?.managerId === filters?.manager;

    return matchesSearch && matchesDepartment && matchesManager;
  }) || [];

  const handleCreateGoal = () => {
    // TODO: Open create goal modal
    console.log('Create goal clicked');
  };

  const handleCreateReviewCycle = () => {
    // TODO: Open create review cycle modal
    console.log('Create review cycle clicked');
  };

  const handleExportReports = () => {
    // TODO: Export performance reports
    console.log('Export reports clicked');
  };

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
  };

  const getEmployeePerformanceData = (employeeId) => {
    return performanceData?.find(p => p?.employeeId === employeeId) || {
      employeeId,
      currentRating: 0,
      goalCompletionRate: 0,
      reviewStatus: 'pending',
      lastReviewDate: null,
      nextReviewDate: null
    };
  };

  const getEmployeeGoals = (employeeId) => {
    return goals?.filter(g => g?.employeeId === employeeId) || [];
  };

  // Performance analytics data
  const performanceStats = {
    averageRating: performanceData?.length > 0 
      ? (performanceData?.reduce((acc, p) => acc + (p?.currentRating || 0), 0) / performanceData?.length)?.toFixed(1)
      : '0.0',
    goalCompletionRate: goals?.length > 0 
      ? Math.round((goals?.filter(g => g?.progress >= 100)?.length / goals?.length) * 100)
      : 0,
    pendingReviews: performanceData?.filter(p => p?.reviewStatus === 'pending')?.length || 0,
    upcomingReviews: performanceData?.filter(p => {
      const nextDate = new Date(p?.nextReviewDate || '');
      const today = new Date();
      const nextWeek = new Date(today?.getTime() + 7 * 24 * 60 * 60 * 1000);
      return nextDate >= today && nextDate <= nextWeek;
    })?.length || 0
  };

  const pageHeaderActions = [
    {
      label: 'Export Reports',
      icon: Download,
      variant: 'outline',
      onClick: handleExportReports
    },
    {
      label: 'Create Review Cycle',
      icon: Calendar,
      variant: 'outline',
      onClick: handleCreateReviewCycle
    },
    {
      label: 'Set Goal',
      icon: Plus,
      onClick: handleCreateGoal
    }
  ];

  // Show API not connected banner
  const apiNotConnected = error && error?.includes('Not implemented');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Performance Management"
        subtitle="Track employee performance, manage goals, and conduct comprehensive reviews"
        actions={pageHeaderActions}
        breadcrumbs={[
          { label: 'HR', href: '/' },
          { label: 'Performance Management' }
        ]}
      />
      {apiNotConnected && (
        <div className="mx-6 mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">
                Backend not configured
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Set VITE_API_URL and wire endpoints to enable performance data.
              </p>
            </div>
          </div>
        </div>
      )}
      <div className="px-6 py-6">
        {/* Performance Dashboard Overview */}
        <PerformanceDashboard 
          stats={performanceStats}
          reviewCycles={reviewCycles}
          recentActivity={performanceData?.slice(0, 5)}
        />

        {/* Navigation Tabs - Desktop */}
        <div className="bg-white rounded-lg shadow-sm mb-6 hidden md:block">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'goals', label: 'Goals', icon: Target },
                { id: 'reviews', label: 'Reviews', icon: Calendar },
                { id: 'analytics', label: 'Analytics', icon: TrendingUp }
              ]?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab?.id
                      ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Mobile Tab Selection */}
        <div className="md:hidden mb-6">
          <Select
            value={activeTab}
            onChange={(e) => setActiveTab(e?.target?.value)}
            className="w-full"
          >
            <option value="overview">Performance Overview</option>
            <option value="goals">Goal Management</option>
            <option value="reviews">Review Cycles</option>
            <option value="analytics">Analytics & Reports</option>
          </Select>
        </div>

        {/* Dual Panel Layout - Desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Employee List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Team Performance</h3>
                  <Button size="sm" variant="ghost">
                    <Filter className="h-4 w-4" />
                  </Button>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    className="pl-10"
                  />
                </div>

                <PerformanceFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                  departments={departments}
                  employees={employees}
                />
              </div>

              <div className="max-h-96 overflow-y-auto">
                {filteredEmployees?.length === 0 ? (
                  <EmptyState
                    icon={Users}
                    title="No employees found"
                    description="Adjust filters or add employees to view performance data."
                    className="py-8"
                  />
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredEmployees?.map((employee) => {
                      const performanceData = getEmployeePerformanceData(employee?.id);
                      const goals = getEmployeeGoals(employee?.id);
                      
                      return (
                        <EmployeePerformanceCard
                          key={employee?.id || Math.random()}
                          employee={employee}
                          performanceData={performanceData}
                          goals={goals}
                          isSelected={selectedEmployee?.id === employee?.id}
                          onClick={() => handleEmployeeSelect(employee)}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Detailed View */}
          <div className="lg:col-span-2">
            {selectedEmployee ? (
              <>
                {activeTab === 'overview' && (
                  <PerformanceOverview
                    employee={selectedEmployee}
                    performanceData={getEmployeePerformanceData(selectedEmployee?.id)}
                    goals={getEmployeeGoals(selectedEmployee?.id)}
                    department={departments?.find(d => d?.id === selectedEmployee?.departmentId)}
                  />
                )}

                {activeTab === 'goals' && (
                  <GoalManagement
                    employee={selectedEmployee}
                    goals={getEmployeeGoals(selectedEmployee?.id)}
                    onGoalCreate={handleCreateGoal}
                  />
                )}

                {activeTab === 'reviews' && (
                  <ReviewCycleManagement
                    employee={selectedEmployee}
                    reviewCycles={reviewCycles}
                    performanceData={getEmployeePerformanceData(selectedEmployee?.id)}
                    onReviewCreate={handleCreateReviewCycle}
                  />
                )}

                {activeTab === 'analytics' && (
                  <PerformanceAnalytics
                    employee={selectedEmployee}
                    performanceHistory={[getEmployeePerformanceData(selectedEmployee?.id)]}
                    goalHistory={getEmployeeGoals(selectedEmployee?.id)}
                  />
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8">
                <EmptyState
                  icon={Target}
                  title="Select an employee"
                  description="Choose an employee from the left panel to view their performance details, goals, and review information."
                />
              </div>
            )}
          </div>
        </div>

        {/* Mobile Layout - Single Panel */}
        <div className="lg:hidden">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {filteredEmployees?.length === 0 ? (
                <EmptyState
                  icon={Users}
                  title="No performance data"
                  description="Add employees and set goals to start tracking performance."
                  primaryAction={{
                    label: 'Set Goals',
                    icon: Plus,
                    onClick: handleCreateGoal
                  }}
                />
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {filteredEmployees?.map((employee) => {
                    const performanceData = getEmployeePerformanceData(employee?.id);
                    const goals = getEmployeeGoals(employee?.id);
                    
                    return (
                      <EmployeePerformanceCard
                        key={employee?.id || Math.random()}
                        employee={employee}
                        performanceData={performanceData}
                        goals={goals}
                        expanded={true}
                        onClick={() => handleEmployeeSelect(employee)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'goals' && (
            <GoalManagement
              goals={goals}
              employees={filteredEmployees}
              onGoalCreate={handleCreateGoal}
            />
          )}

          {activeTab === 'reviews' && (
            <ReviewCycleManagement
              reviewCycles={reviewCycles}
              employees={filteredEmployees}
              onReviewCreate={handleCreateReviewCycle}
            />
          )}

          {activeTab === 'analytics' && (
            <PerformanceAnalytics
              performanceData={performanceData}
              goals={goals}
              employees={filteredEmployees}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PerformanceManagement;